/**
 * Side-by-Side Diff View
 * 
 * Shows current document vs preview with AI changes applied.
 * GitHub-style diff with synchronized scrolling, operation checkboxes, and enhanced visual feedback.
 * FAA-compliant change tracking for certification packages.
 */

import { useEditor, EditorContent } from '@tiptap/react';
import { logger } from '../../utils/logger';
import StarterKit from '@tiptap/starter-kit';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import Image from '@tiptap/extension-image';
import Heading from '@tiptap/extension-heading';
import { useEffect, useState, useRef } from 'react';
import type { AeroDocument, ParagraphContent, ListContent, ListItem, TableContent } from '../../types/document-schema';
import type { DocumentOperation } from '../../types/document-operations';
import { DiffHighlight } from './DiffHighlight';
import { EditorSidebar } from './EditorSidebar';
import { EditorToolbar } from './EditorToolbar';
import { describeOperation } from '../../types/document-operations';
import { convertTipTapToDocument } from '../../utils/tiptap-to-document';
import type { TipTapDoc, TipTapNode } from '../../utils/document-to-tiptap';
import { Button } from '@/components/ui';
import type { Editor } from '@tiptap/core';

export interface DiffViewProps {
  originalDocument: AeroDocument;
  previewDocument: AeroDocument;
  operations: DocumentOperation[];
  onAcceptSelected?: (operationIndices: number[]) => void;
  onRejectAll?: () => void;
  isApplying?: boolean;
  onCloseSplitView?: () => void;
  showOperationsSidebar?: boolean;
  onToggleSplitView?: () => void;
  splitViewEnabled?: boolean;
  onWorkingCopyUpdate?: (document: AeroDocument) => void;
  onCatchupBaseline?: () => void;
  onAddSection?: (sectionType: 'section' | 'subsection' | 'appendix', title: string, parentId?: string) => void;
  documentTitle?: string;
  onBack?: () => void;
  saving?: boolean;
  error?: string | null;
  lastSaved?: Date | null;
  isOnline?: boolean;
}

export function DiffView({
  originalDocument,
  previewDocument,
  operations,
  onAcceptSelected,
  onRejectAll,
  isApplying = false,
  onCloseSplitView: _onCloseSplitView,
  showOperationsSidebar = true,
  onToggleSplitView,
  splitViewEnabled,
  onWorkingCopyUpdate,
  onCatchupBaseline,
  onAddSection,
  documentTitle,
  onBack,
  saving = false,
  error,
  lastSaved,
  isOnline = true
}: DiffViewProps) {
  // DEBUG: Log when component receives props
  logger.debug('[DiffView] ========== COMPONENT RENDER ==========');
  logger.debug('[DiffView] Props received:', {
    originalBlocks: originalDocument.body.reduce((sum, s) => sum + s.blocks.length, 0),
    previewBlocks: previewDocument.body.reduce((sum, s) => sum + s.blocks.length, 0),
    operationsCount: operations.length,
    showOperationsSidebar
  });
  
  const [selectedOperations, setSelectedOperations] = useState<Set<number>>(
    new Set(operations.map((_, i) => i)) // All checked by default
  );
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncEnabled, setSyncEnabled] = useState(true); // User can toggle sync on/off
  
  const isApplyingHighlightsRef = useRef(false);
  const leftScrollRef = useRef<HTMLDivElement>(null);
  const rightScrollRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Convert documents to TipTap format (reuse from StructuredEditor)
  logger.debug('[DiffView] Converting documents to TipTap...');
  const originalContent = convertDocumentToTipTap(originalDocument);
  const previewContent = convertDocumentToTipTap(previewDocument);
  logger.debug('[DiffView] Conversion complete');
  logger.debug('[DiffView] Original HTML length:', originalContent.length);
  logger.debug('[DiffView] Preview HTML length:', previewContent.length);
  
  // Create editors
  // CRITICAL: immediatelyRender: false is required for React 18 Strict Mode compatibility
  // Without this, operations may be applied multiple times due to double-render
  const originalEditor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({ heading: false }),
      Heading.configure({
        levels: [1, 2, 3, 4, 5, 6], // Support all 6 heading levels for deep section nesting
      }).extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            id: {
              default: null,
              parseHTML: element => element.getAttribute('id'),
              renderHTML: attributes => attributes.id ? { id: attributes.id } : {},
            },
          };
        },
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Image,
      DiffHighlight,
    ],
    content: originalContent,
    editable: false,
  });
  
  const previewEditor = useEditor({
    immediatelyRender: false,
    extensions: [
      StarterKit.configure({ heading: false }),
      Heading.configure({
        levels: [1, 2, 3, 4, 5, 6], // Support all 6 heading levels for deep section nesting
      }).extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            id: {
              default: null,
              parseHTML: element => element.getAttribute('id'),
              renderHTML: attributes => attributes.id ? { id: attributes.id } : {},
            },
          };
        },
      }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Image,
      DiffHighlight,
    ],
    content: previewContent,
    editable: true, // Editable - user can adjust AI output or edit directly
    onUpdate: ({ editor }) => {
      // Skip re-conversion while diff highlights are being applied
      if (isApplyingHighlightsRef.current) return;
      // Convert TipTap content back to AeroDocument and notify parent
      if (onWorkingCopyUpdate) {
        const updatedDoc = convertTipTapToDocument(editor, previewDocument);
        onWorkingCopyUpdate(updatedDoc);
      }
    },
  });
  
  // Apply section-level diff highlights to both editors
  useEffect(() => {
    if (originalEditor && previewEditor) {
      isApplyingHighlightsRef.current = true;
      applyDiffHighlights(originalEditor, previewEditor, originalDocument, previewDocument);
      isApplyingHighlightsRef.current = false;
    }
  }, [originalEditor, previewEditor, originalDocument, previewDocument]);
  
  // Debounced section-aligned synchronized scrolling
  const handleLeftScroll = () => {
    if (!syncEnabled || isSyncing) return;
    
    // Clear any pending sync
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Debounce: only sync after user stops scrolling for 100ms
    scrollTimeoutRef.current = setTimeout(() => {
      if (!leftScrollRef.current || !rightScrollRef.current || !originalEditor || !previewEditor) return;
      
      setIsSyncing(true);
      
      const leftContainer = leftScrollRef.current;
      const rightContainer = rightScrollRef.current;
      
      // Use simple percentage-based scrolling for smoothness
      const scrollPercent = leftContainer.scrollTop / (leftContainer.scrollHeight - leftContainer.clientHeight || 1);
      const targetScroll = scrollPercent * (rightContainer.scrollHeight - rightContainer.clientHeight || 1);
      
      // Animate the sync for smooth catchup
      rightContainer.scrollTo({
        top: Math.max(0, targetScroll),
        behavior: 'smooth'
      });
      
      setTimeout(() => setIsSyncing(false), 100);
    }, 150); // 150ms debounce
  };
  
  const handleRightScroll = () => {
    if (!syncEnabled || isSyncing) return;
    
    // Clear any pending sync
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // Debounce: only sync after user stops scrolling for 100ms
    scrollTimeoutRef.current = setTimeout(() => {
      if (!leftScrollRef.current || !rightScrollRef.current || !originalEditor || !previewEditor) return;
      
      setIsSyncing(true);
      
      const leftContainer = leftScrollRef.current;
      const rightContainer = rightScrollRef.current;
      
      // Use simple percentage-based scrolling for smoothness
      const scrollPercent = rightContainer.scrollTop / (rightContainer.scrollHeight - rightContainer.clientHeight || 1);
      const targetScroll = scrollPercent * (leftContainer.scrollHeight - leftContainer.clientHeight || 1);
      
      // Animate the sync for smooth catchup
      leftContainer.scrollTo({
        top: Math.max(0, targetScroll),
        behavior: 'smooth'
      });
      
      setTimeout(() => setIsSyncing(false), 100);
    }, 150); // 150ms debounce
  };
  
  const toggleOperation = (index: number) => {
    const newSelected = new Set(selectedOperations);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedOperations(newSelected);
  };
  
  const selectAll = () => {
    setSelectedOperations(new Set(operations.map((_, i) => i)));
  };
  
  const deselectAll = () => {
    setSelectedOperations(new Set());
  };
  
  const handleAccept = () => {
    const indices = Array.from(selectedOperations).sort((a, b) => a - b);
    onAcceptSelected(indices);
  };
  
  if (!originalEditor || !previewEditor) {
    return <div className="loading">Loading diff view...</div>;
  }
  
  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Document header bar - matches StructuredEditor */}
      {documentTitle && onBack && (
        <div className="bg-popover text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="text-white hover:text-muted-foreground">
              ← Back
            </button>
            <div>
              <h1 className="text-lg font-semibold">{documentTitle}</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {saving && (
              <span className="text-warning animate-pulse text-sm">Saving...</span>
            )}
            {!saving && lastSaved && isOnline && (
              <span className="text-white text-opacity-70 text-sm">
                Last saved {lastSaved.toLocaleTimeString()}
              </span>
            )}
            {!isOnline && (
              <span className="text-warning text-sm">Offline - saving locally</span>
            )}
            {error && (
              <span className="text-destructive text-sm">{error}</span>
            )}
          </div>
        </div>
      )}
      
      {/* Toolbar area - two rows for clean layout */}
      <div className="bg-card border-b border-border flex-shrink-0">
        {/* First row: Split view controls and auto-save (solid bar) */}
        <div className="p-3 flex items-center gap-2 border-b border-border">
          {/* Split View toggle */}
          {onToggleSplitView && (
            <button
              onClick={onToggleSplitView}
              className={`btn btn-sm ${splitViewEnabled ? 'bg-success text-success-foreground hover:bg-success/90' : 'btn-secondary'}`}
              title={splitViewEnabled ? 'Close split view' : 'Open split view'}
            >
              {splitViewEnabled ? '✕ Close Split' : 'Split View'}
            </button>
          )}
          
          <div className="w-px h-6 bg-secondary mx-2"></div>
          
          {/* Sync toggle */}
          <button
            onClick={() => setSyncEnabled(!syncEnabled)}
            className={`btn btn-sm ${syncEnabled ? 'bg-success text-success-foreground hover:bg-success/90' : 'btn-secondary'}`}
            title={syncEnabled ? 'Disable synchronized scrolling' : 'Enable synchronized scrolling'}
          >
            Scroll Sync
          </button>
          
          {/* Catchup baseline button - only show when not in operations mode */}
          {!showOperationsSidebar && onCatchupBaseline && (
            <>
              <div className="w-px h-6 bg-secondary mx-2"></div>
              <Button
                variant="secondary"
                size="sm"
                onClick={onCatchupBaseline}
                title="Update baseline to match current working copy"
              >
                ↻ Catchup Baseline
              </Button>
            </>
          )}
          
          <div className="flex-1"></div>
          
          {/* Auto-save status indicator - always visible */}
          <div className="text-sm mr-4">
            {saving && <span className="text-warning animate-pulse">Saving...</span>}
            {!saving && lastSaved && isOnline && (
              <span className="text-muted-foreground">
                Saved {lastSaved.toLocaleTimeString()}
              </span>
            )}
            {!saving && !lastSaved && isOnline && (
              <span className="text-success">Auto-save active</span>
            )}
            {!isOnline && (
              <span className="text-warning">Offline - saving locally</span>
            )}
          </div>
          
          {/* Action buttons on right */}
          {showOperationsSidebar && operations.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {selectedOperations.size} of {operations.length} selected
              </span>
              
              {onAcceptSelected && (
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleAccept}
                  disabled={isApplying || selectedOperations.size === 0}
                >
                  {isApplying ? 'Applying...' : `Accept Selected (${selectedOperations.size})`}
                </Button>
              )}
              
              {onRejectAll && (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={onRejectAll}
                  disabled={isApplying}
                >
                  Reject All
                </Button>
              )}
            </div>
          )}
        </div>
        
        {/* Second row: Editor formatting toolbar (solid bar, aligns with right panel) */}
        {previewEditor && (
          <EditorToolbar
            editor={previewEditor}
            document={previewDocument}
            onSave={() => {}} // No-op, auto-save handles this
            hideAutoSaveIndicator={true}
            hideDocumentInfo={true}
          />
        )}
      </div>
      
      {/* Main content area */}
      <div className="flex flex-1 min-h-0">
        {/* TOC shows preview document structure */}
        <EditorSidebar document={previewDocument} editor={previewEditor} onAddSection={onAddSection} />
        
        {/* Left panel: Current document (read-only) */}
        <div className="flex-1 flex flex-col border-r border-border">
          <div className="bg-muted px-4 py-2 border-b border-border flex-shrink-0">
            <h3 className="text-sm font-semibold text-foreground">Current Document</h3>
          </div>
          
          <div
            ref={leftScrollRef}
            onScroll={handleLeftScroll}
            className="flex-1 overflow-y-auto bg-card p-8"
          >
            <div className="w-full">
              <EditorContent editor={originalEditor} className="prose max-w-none" />
            </div>
          </div>
        </div>
        
        {/* Right panel: Preview with AI changes (editable) */}
        <div className="flex-1 flex flex-col border-r border-border">
          <div className="bg-success-light px-4 py-2 border-b border-border flex-shrink-0">
            <h3 className="text-sm font-semibold text-success">With AI Changes (Editable)</h3>
          </div>
          
          <div
            ref={rightScrollRef}
            onScroll={handleRightScroll}
            className="flex-1 overflow-y-auto bg-card p-8"
          >
            <div className="w-full">
              <EditorContent editor={previewEditor} className="prose max-w-none" />
            </div>
          </div>
        </div>
        
        {/* Operations sidebar - only show when reviewing AI edits */}
        {showOperationsSidebar && operations.length > 0 && (
          <div className="w-[250px] bg-muted border-l border-border flex flex-col overflow-hidden">
          <div className="p-4 border-b border-border flex-shrink-0">
            <h3 className="font-semibold text-foreground mb-2">Operations</h3>
            <div className="flex gap-2 text-xs">
              <button onClick={selectAll} className="text-link hover:underline">
                Select All
              </button>
              <span className="text-muted-foreground">|</span>
              <button onClick={deselectAll} className="text-link hover:underline">
                Deselect All
              </button>
            </div>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {operations.map((op, index) => (
              <div
                key={index}
                className={`p-3 border rounded cursor-pointer transition-colors ${
                  selectedOperations.has(index)
                    ? 'bg-info border-primary'
                    : 'bg-card border-border hover:border-border'
                }`}
                onClick={() => toggleOperation(index)}
              >
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={selectedOperations.has(index)}
                    onChange={() => toggleOperation(index)}
                    className="mt-1"
                    onClick={(e) => e.stopPropagation()}
                  />
                  
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-foreground mb-1">
                      {op.op.replace(/_/g, ' ').toUpperCase()}
                    </div>
                    <div className="text-xs text-muted-foreground break-words">
                      {describeOperation(op)}
                    </div>
                    {op.explanation && (
                      <div className="text-xs text-muted-foreground italic mt-1">
                        {op.explanation}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <div className="p-4 border-t border-border flex-shrink-0 bg-muted">
            <div className="text-xs text-muted-foreground">
              {selectedOperations.size} operation{selectedOperations.size !== 1 ? 's' : ''} selected
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
}

// ===== Section-Level Diff Computation =====

interface SectionDiff {
  sectionId: string;
  type: 'added' | 'removed' | 'modified';
}

/**
 * Compute section-level diffs between two documents.
 * Compares sections by ID and stringified block content.
 */
function computeSectionDiffs(
  originalDoc: AeroDocument,
  previewDoc: AeroDocument
): SectionDiff[] {
  const diffs: SectionDiff[] = [];
  
  const originalMap = new Map(originalDoc.body.map(s => [s.id, s]));
  const previewMap = new Map(previewDoc.body.map(s => [s.id, s]));
  
  // Check for added and modified sections (exist in preview)
  for (const [id, previewSection] of previewMap) {
    const originalSection = originalMap.get(id);
    if (!originalSection) {
      diffs.push({ sectionId: id, type: 'added' });
    } else {
      const originalBlocks = JSON.stringify(originalSection.blocks);
      const previewBlocks = JSON.stringify(previewSection.blocks);
      const titleChanged = originalSection.title !== previewSection.title;
      
      if (originalBlocks !== previewBlocks || titleChanged) {
        diffs.push({ sectionId: id, type: 'modified' });
      }
    }
  }
  
  // Check for removed sections (exist in original but not preview)
  for (const id of originalMap.keys()) {
    if (!previewMap.has(id)) {
      diffs.push({ sectionId: id, type: 'removed' });
    }
  }
  
  return diffs;
}

/**
 * Find section heading ranges in a TipTap editor document.
 * Each range spans from a section heading to the start of the next section heading.
 */
function findSectionRanges(editor: Editor): Array<{ sectionId: string; from: number; to: number }> {
  const ranges: Array<{ sectionId: string; from: number; to: number }> = [];
  
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name === 'heading' && node.attrs.id?.startsWith('sec-')) {
      ranges.push({
        sectionId: node.attrs.id,
        from: pos,
        to: pos + node.nodeSize,
      });
    }
  });
  
  // Extend each range to the start of the next section heading or end of doc
  for (let i = 0; i < ranges.length; i++) {
    ranges[i].to = i + 1 < ranges.length
      ? ranges[i + 1].from
      : editor.state.doc.content.size;
  }
  
  return ranges;
}

/**
 * Apply diff highlight marks to an editor for the given section diffs.
 * Only highlights sections whose diff type is in the highlightTypes set.
 */
function applyDiffMarksToEditor(
  editor: Editor,
  diffs: SectionDiff[],
  highlightTypes: Set<SectionDiff['type']>
): void {
  const ranges = findSectionRanges(editor);
  const diffMap = new Map(diffs.map(d => [d.sectionId, d.type]));
  
  const { state } = editor;
  const { tr } = state;
  const diffMark = state.schema.marks.diffHighlight;
  
  if (!diffMark) {
    logger.warn('[DiffView] DiffHighlight mark not found in schema');
    return;
  }
  
  // Clear existing diff highlights
  for (const range of ranges) {
    tr.removeMark(range.from, range.to, diffMark);
  }
  
  // Apply new highlights based on section diffs
  for (const range of ranges) {
    const diffType = diffMap.get(range.sectionId);
    if (!diffType || !highlightTypes.has(diffType)) continue;
    
    const markType = diffType === 'added' ? 'addition'
      : diffType === 'modified' ? 'modification'
      : 'deletion';
    
    tr.addMark(range.from, range.to, diffMark.create({ type: markType }));
  }
  
  if (tr.steps.length > 0) {
    editor.view.dispatch(tr);
  }
}

/**
 * Apply section-level diff highlights to both editors.
 * - Preview editor: highlights added (green) and modified (yellow) sections
 * - Original editor: highlights removed (red) sections
 */
function applyDiffHighlights(
  originalEditor: Editor | null,
  previewEditor: Editor | null,
  originalDoc: AeroDocument,
  previewDoc: AeroDocument
): void {
  if (!previewEditor) return;
  
  const diffs = computeSectionDiffs(originalDoc, previewDoc);
  
  logger.debug('[DiffView] Section diffs computed:', {
    total: diffs.length,
    added: diffs.filter(d => d.type === 'added').length,
    removed: diffs.filter(d => d.type === 'removed').length,
    modified: diffs.filter(d => d.type === 'modified').length,
  });
  
  if (diffs.length === 0) return;
  
  // Highlight added and modified sections in the preview editor
  applyDiffMarksToEditor(previewEditor, diffs, new Set(['added', 'modified']));
  
  // Highlight removed sections in the original editor
  if (originalEditor) {
    applyDiffMarksToEditor(originalEditor, diffs, new Set(['removed']));
  }
}

/**
 * Convert document to TipTap format (simplified version from StructuredEditor)
 * TODO: Extract this to shared utility to avoid duplication
 */
function convertDocumentToTipTap(document: AeroDocument): TipTapDoc {
  logger.debug('[DiffView/Converter] Converting document:', document.metadata.documentNumber);
  logger.debug('[DiffView/Converter] Sections:', document.body.length);
  logger.debug('[DiffView/Converter] Total blocks:', document.body.reduce((sum, s) => sum + s.blocks.length, 0));
  
  const content: TipTapNode[] = [];
  
  const topLevelSections = document.body.filter(s => !s.parent);
  logger.debug('[DiffView/Converter] Top-level sections:', topLevelSections.length);
  
  for (const section of topLevelSections) {
    renderSectionAndChildren(section, document.body, content);
  }
  
  logger.debug('[DiffView/Converter] Generated TipTap nodes:', content.length);
  
  return {
    type: 'doc',
    content
  };
}

import type { DocumentSection, ContentBlock } from '../../types/document-schema';

function renderSectionAndChildren(section: DocumentSection, allSections: DocumentSection[], content: TipTapNode[]): void {
  logger.debug('[DiffView/Render] Rendering section:', section.id, section.title, 'blocks:', section.blocks.length);
  
  // Use section number to derive heading level (authoritative source for hierarchy depth)
  const headingLevel = getHeadingLevelFromSectionNumber(section.number);
  content.push({
    type: 'heading',
    attrs: { 
      level: headingLevel,
      id: section.id
    },
    content: [
      {
        type: 'text',
        text: `${section.number} ${section.title}`
      }
    ]
  });
  
  logger.debug('[DiffView/Render] Converting', section.blocks.length, 'blocks to TipTap');
  let renderedBlocks = 0;
  
  for (const block of section.blocks) {
    logger.debug('[DiffView/Render]   Block:', block.id, 'type:', block.type);
    const tipTapNode = convertBlockToTipTap(block);
    if (tipTapNode) {
      content.push(tipTapNode);
      renderedBlocks++;
    } else {
      logger.warn('[DiffView/Render]   ⚠️ Block converted to null (empty content)');
    }
  }
  
  logger.debug('[DiffView/Render] Rendered', renderedBlocks, 'of', section.blocks.length, 'blocks');
  
  if (section.children && section.children.length > 0) {
    for (const childId of section.children) {
      const childSection = allSections.find((s: DocumentSection) => s.id === childId);
      if (childSection) {
        renderSectionAndChildren(childSection, allSections, content);
      }
    }
  }
}

/**
 * Get heading level from section number.
 * Section number (e.g., "1.2.3") is the authoritative source for hierarchy depth.
 * "1" → h1, "1.2" → h2, "1.2.3" → h3, etc.
 * Caps at h6 since HTML only supports h1-h6.
 */
function getHeadingLevelFromSectionNumber(sectionNumber: string): 1 | 2 | 3 | 4 | 5 | 6 {
  if (!sectionNumber) return 2;
  const parts = sectionNumber.split('.');
  const depth = parts.length;
  return Math.min(depth, 6) as 1 | 2 | 3 | 4 | 5 | 6;
}

function convertBlockToTipTap(block: ContentBlock): TipTapNode | null {
  switch (block.type) {
    case 'paragraph': {
      const paragraphContent = block.content as ParagraphContent;
      const paragraphText = paragraphContent?.text || '';
      if (!paragraphText.trim()) return null;
      return {
        type: 'paragraph',
        content: [{ type: 'text', text: paragraphText }]
      };
    }
    
    case 'heading': {
      const headingContent = block.content as { text: string; level?: number };
      const headingText = headingContent?.text || '';
      if (!headingText.trim()) return null;
      return {
        type: 'heading',
        attrs: { level: headingContent.level || 2 },
        content: [{ type: 'text', text: headingText }]
      };
    }
    
    case 'bulleted_list': {
      const listContent = block.content as ListContent;
      return {
        type: 'bulletList',
        content: listContent.items?.map((item: ListItem) => ({
          type: 'listItem',
          content: [
            {
              type: 'paragraph',
              content: [{ type: 'text', text: item.text }]
            }
          ]
        })) || []
      };
    }
    
    case 'numbered_list': {
      const numberedContent = block.content as ListContent;
      return {
        type: 'orderedList',
        content: numberedContent.items?.map((item: ListItem) => ({
          type: 'listItem',
          content: [
            {
              type: 'paragraph',
              content: [{ type: 'text', text: item.text }]
            }
          ]
        })) || []
      };
    }
    
    case 'table': {
      const tableContent = block.content as TableContent;
      
      // Helper to safely get cell value - handles both formats:
      // 1. Direct string: row.cells[col.id] = "value"
      // 2. Object format: row.cells[col.id] = { value: "value" }
      const getCellValue = (row: TableRow, colId: string): string => {
        const cell = row.cells?.[colId];
        if (!cell) return ' '; // TipTap requires non-empty text
        if (typeof cell === 'string') return cell || ' ';
        if (typeof cell === 'object' && 'value' in cell) return (cell as { value: string }).value || ' ';
        return ' ';
      };
      
      // Helper to get column header - handles both 'label' and 'header' fields
      const getColumnHeader = (col: TableColumn): string => {
        return col.label || col.header || ' ';
      };
      
      return {
        type: 'table',
        content: [
          {
            type: 'tableRow',
            content: tableContent.columns?.map((col) => ({
              type: 'tableHeader',
              content: [
                {
                  type: 'paragraph',
                  content: [{ type: 'text', text: getColumnHeader(col) }]
                }
              ]
            })) || []
          },
          ...tableContent.rows?.map((row) => ({
            type: 'tableRow',
            content: tableContent.columns?.map((col) => ({
              type: 'tableCell',
              content: [
                {
                  type: 'paragraph',
                  content: [{ type: 'text', text: getCellValue(row, col.id) }]
                }
              ]
            })) || []
          })) || []
        ]
      };
    }
    
    case 'image':
      return {
        type: 'image',
        attrs: {
          src: block.content.url || block.content.s3Key,
          alt: block.content.alt || '',
          title: block.content.caption || ''
        }
      };
    
    default:
      return null;
  }
}
